import { AfterViewInit, Directive, OnDestroy, Optional } from '@angular/core';
import { Popover } from 'primeng/popover';
import { Subscription } from 'rxjs';

/**
 * Monkey-patch PrimeNG Popover.show to queue show() calls when already visible.
 * Replays the last queued show after the current popover fully hides (onHide),
 * working around the PrimeNG visibility timing bug.
 *
 * Usage:
 *   <p-popover popoverQueuedShow></p-popover>
 */
@Directive({
  selector: '[popoverQueuedShow]'
})
export class PopoverQueuedShowDirective implements AfterViewInit, OnDestroy {
  private originalShow?: (...args: any[]) => void;
  private queuedArgs: any[] | null = null;
  private hideSub?: Subscription;

  constructor(@Optional() private popover: Popover) {}

  ngAfterViewInit(): void {
    if (!this.popover) {
      // Using console.warn here is intentional for dev visibility; safe in prod as no-op
      console.warn('popoverQueuedShow used outside of a Popover instance');
      return;
    }

    const pop = this.popover;

    // Preserve original show with correct `this`
    const original = pop.show.bind(pop);
    this.originalShow = original;

    // Monkey-patch show to queue when already visible
    (pop as any).show = (...args: any[]) => {
      if (pop.overlayVisible) {
        this.queuedArgs = args;
        pop.hide(); // will trigger onHide -> replay
        return;
      }
      original.apply(null, args as [any, any?]);
    };

    // After hide completes, replay most recent queued show (if any)
    this.hideSub = (pop as any).onHide?.subscribe?.(() => {
      if (!this.queuedArgs) return;
      const args = this.queuedArgs;
      this.queuedArgs = null;
      // Use microtask to ensure overlay teardown finished before re-show
      queueMicrotask(() => {
        if (this.originalShow) {
          this.originalShow!.apply(null, args as [any, any?]);
        }
      });
    });
  }

  ngOnDestroy(): void {
    if (this.popover && this.originalShow) {
      (this.popover as any).show = this.originalShow;
    }
    this.hideSub?.unsubscribe();
    this.queuedArgs = null;
  }
}

